import {EmptyPolicyNumber, PolicyNumber, ValidPolicyNumber} from 'ApplicationEntry/models/EnteredPolicyNumber';
import {PackageModel, UnassignedPackageModel} from 'ApplicationEntry/models/ProductsRiders/PackageModel';
import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';

type CriticalIllnessNYProps = {
  planType: StringUnion;
  benefitAmount: number;
  premium: StringUnion;
  calculatedPremium: StringUnion;
  cwa: StringUnion;
  insuredSC: StringUnion;
  spouseSC: StringUnion;
  section125: boolean;
  replacement: boolean;
  policyNumber: PolicyNumber;
  situsState: StringUnion;
  wellnessUnits: StringUnion;
  ciOption: boolean;
  secondEventCI: boolean;
  jStamp: boolean;
  packageSource: StringUnion;
  packageSourceList: PackageModel;
};

export class InvalidGroupCriticalIllnessNY {
  public readonly key = 'InvalidGroupCriticalIllnessNY';

  constructor(
    public readonly planType: StringUnion,
    public readonly benefitAmount: number,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly insuredSC: StringUnion,
    public readonly spouseSC: StringUnion,
    public readonly section125: boolean,
    public readonly replacement: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly situsState: StringUnion,
    public readonly wellnessUnits: StringUnion,
    public readonly ciOption: boolean,
    public readonly secondEventCI: boolean,
    public readonly jStamp: boolean,
    public readonly packageSource: StringUnion,
    public readonly packageSourceList: PackageModel
  ) {}
}

export class ValidGroupCriticalIllnessNY {
  public static createApp({
    planType,
    benefitAmount,
    premium,
    calculatedPremium,
    cwa,
    insuredSC,
    spouseSC,
    section125,
    replacement,
    policyNumber,
    situsState,
    wellnessUnits,
    ciOption,
    secondEventCI,
    jStamp,
    packageSource,
    packageSourceList,
  }: CriticalIllnessNYProps) {
    return this.create(
      planType,
      benefitAmount,
      premium,
      calculatedPremium,
      cwa,
      insuredSC,
      spouseSC,
      section125,
      replacement,
      policyNumber,
      situsState,
      wellnessUnits,
      ciOption,
      secondEventCI,
      jStamp,
      packageSource,
      packageSourceList
    );
  }

  public static create(
    planType: StringUnion,
    benefitAmount: number,
    premium: StringUnion,
    calculatedPremium: StringUnion,
    cwa: StringUnion,
    insuredSC: StringUnion,
    spouseSC: StringUnion,
    section125: boolean,
    replacement: boolean,
    policyNumber: PolicyNumber,
    situsState: StringUnion,
    wellnessUnits: StringUnion,
    ciOption: boolean,
    secondEventCI: boolean,
    jStamp: boolean,
    packageSource: StringUnion,
    packageSourceList: PackageModel
  ) {
    return planType &&
      benefitAmount &&
      isNonEmptyString(premium) &&
      isNonEmptyString(cwa) &&
      insuredSC &&
      section125 &&
      replacement &&
      policyNumber
      ? new ValidGroupCriticalIllnessNY(
          planType,
          benefitAmount,
          premium,
          calculatedPremium,
          cwa,
          insuredSC,
          spouseSC,
          section125,
          replacement,
          policyNumber,
          situsState,
          wellnessUnits,
          ciOption,
          secondEventCI,
          jStamp,
          packageSource,
          packageSourceList
        )
      : new InvalidGroupCriticalIllnessNY(
          planType,
          benefitAmount,
          premium,
          calculatedPremium,
          cwa,
          insuredSC,
          spouseSC,
          section125,
          replacement,
          policyNumber,
          situsState,
          wellnessUnits,
          ciOption,
          secondEventCI,
          jStamp,
          packageSource,
          packageSourceList
        );
  }

  public readonly key = 'ValidGroupCriticalIllnessNY';
  constructor(
    public readonly planType: StringUnion,
    public readonly benefitAmount: number,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly insuredSC: StringUnion,
    public readonly spouseSC: StringUnion,
    public readonly section125: boolean,
    public readonly replacement: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly situsState: StringUnion,
    public readonly wellnessUnits: StringUnion,
    public readonly ciOption: boolean,
    public readonly secondEventCI: boolean,
    public readonly jStamp: boolean,
    public readonly packageSource: StringUnion,
    public readonly packageSourceList: PackageModel
  ) {}
}

export type GroupCriticalIllnessNY = InvalidGroupCriticalIllnessNY | ValidGroupCriticalIllnessNY;

export type GroupCriticalIllnessNYOptions<TReturn> = ToPartialMatchOptions<GroupCriticalIllnessNY, 'key', TReturn>;

export const matchGroupCriticalIllnessNY = <TReturn>(options: GroupCriticalIllnessNYOptions<TReturn>) => (
  app: GroupCriticalIllnessNY
): TReturn => {
  switch (app.key) {
    case 'InvalidGroupCriticalIllnessNY':
      return options.InvalidGroupCriticalIllnessNY ? options.InvalidGroupCriticalIllnessNY(app) : options.default(app);
    case 'ValidGroupCriticalIllnessNY':
      return options.ValidGroupCriticalIllnessNY ? options.ValidGroupCriticalIllnessNY(app) : options.default(app);
  }
};

export const EmptyGroupCriticalIllnessNY = new InvalidGroupCriticalIllnessNY(
  new EmptyString(),
  0,
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  false,
  EmptyPolicyNumber,
  new EmptyString(),
  new EmptyString(),
  false,
  false,
  false,
  new EmptyString(),
  new UnassignedPackageModel()
);

export const changePlanType = (planType: string) => (app: GroupCriticalIllnessNY) =>
  ValidGroupCriticalIllnessNY.createApp({...app, planType: NonEmptyString.create(planType)});

export const changeBenefitAmount = (benefitAmount: number) => (app: GroupCriticalIllnessNY) =>
  ValidGroupCriticalIllnessNY.createApp({...app, benefitAmount});

export const changePremium = (premium: string) => (app: GroupCriticalIllnessNY) =>
  ValidGroupCriticalIllnessNY.createApp({...app, premium: NonEmptyString.create(premium)});

export const changeCalculatedPremium = (calculatedPremium: string) => (app: GroupCriticalIllnessNY) =>
  ValidGroupCriticalIllnessNY.createApp({...app, premium: NonEmptyString.create(calculatedPremium)});

export const changeCWA = (cwa: string) => (app: GroupCriticalIllnessNY) =>
  ValidGroupCriticalIllnessNY.createApp({...app, cwa: NonEmptyString.create(cwa)});

export const changeInsuredSC = (insuredSC: string) => (app: GroupCriticalIllnessNY) =>
  ValidGroupCriticalIllnessNY.createApp({...app, insuredSC: NonEmptyString.create(insuredSC)});

export const changeSpouseSC = (spouseSC: string) => (app: GroupCriticalIllnessNY) =>
  ValidGroupCriticalIllnessNY.createApp({...app, spouseSC: NonEmptyString.create(spouseSC)});

export const changeSection125 = (section125: boolean) => (app: GroupCriticalIllnessNY) =>
  ValidGroupCriticalIllnessNY.createApp({...app, section125});

export const changeReplacement = (replacement: boolean) => (app: GroupCriticalIllnessNY) =>
  ValidGroupCriticalIllnessNY.createApp({
    ...app,
    replacement,
    policyNumber: ValidPolicyNumber.create(new EmptyString()),
  });

export const changePolicyNumber = (policyNumber: StringUnion) => (app: GroupCriticalIllnessNY) =>
  ValidGroupCriticalIllnessNY.createApp({...app, policyNumber: ValidPolicyNumber.create(policyNumber)});

export const changeSitusState = (situsState: string) => (app: GroupCriticalIllnessNY) =>
  ValidGroupCriticalIllnessNY.createApp({...app, situsState: NonEmptyString.create(situsState)});

export const changeWellnessUnit = (wellnessUnits: string) => (app: GroupCriticalIllnessNY) =>
  ValidGroupCriticalIllnessNY.createApp({...app, wellnessUnits: NonEmptyString.create(wellnessUnits)});

export const changeCIOption = (ciOption: boolean) => (app: GroupCriticalIllnessNY) =>
  ValidGroupCriticalIllnessNY.createApp({...app, ciOption});

export const changeSecondEventCI = (secondEventCI: boolean) => (app: GroupCriticalIllnessNY) =>
  ValidGroupCriticalIllnessNY.createApp({...app, secondEventCI});

export const changeJStamp = (jStamp: boolean) => (app: GroupCriticalIllnessNY) =>
  ValidGroupCriticalIllnessNY.createApp({...app, jStamp});

export const changePackageSource = (packageSource: string) => (app: GroupCriticalIllnessNY) =>
  ValidGroupCriticalIllnessNY.createApp({...app, packageSource: NonEmptyString.create(packageSource)});

export const changePackageSourceList = (packageSourceList: PackageModel) => (app: GroupCriticalIllnessNY) =>
  ValidGroupCriticalIllnessNY.createApp({...app, packageSourceList});
